import { useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { useGame } from '../context/GameContext';
import { usePlayer } from '../context/PlayerContext';

function useGameRound() {
  const { state } = useGame();
  const { playerId, setDrawnCard, setDrawnFrom } = usePlayer();
  const { roomCode } = state;

  const callFunction = useCallback(
    async (name, data = {}) => {
      const fn = httpsCallable(functions, name);
      const result = await fn({ roomCode, playerId, ...data });
      return result.data;
    },
    [roomCode, playerId]
  );

  const createRoom = useCallback(
    async (displayName) => {
      const fn = httpsCallable(functions, 'createRoom');
      return (await fn({ displayName })).data;
    },
    []
  );

  const joinRoom = useCallback(
    async (code, displayName) => {
      const fn = httpsCallable(functions, 'joinRoom');
      return (await fn({ roomCode: code, displayName })).data;
    },
    []
  );

  const markReady = useCallback(
    () => callFunction('markReady'),
    [callFunction]
  );

  const startGame = useCallback(
    () => callFunction('startGame'),
    [callFunction]
  );

  const revealInitialCards = useCallback(
    (positions) => callFunction('revealInitialCards', { positions }),
    [callFunction]
  );

  const performDraw = useCallback(
    async (source) => {
      const result = await callFunction('performDraw', { source });
      if (result.card) {
        setDrawnCard(result.card);
        setDrawnFrom(source);
      }
      return result;
    },
    [callFunction, setDrawnCard, setDrawnFrom]
  );

  const performSwap = useCallback(
    async (handPosition) => {
      const result = await callFunction('performSwap', { handPosition });
      setDrawnCard(null);
      setDrawnFrom(null);
      return result;
    },
    [callFunction, setDrawnCard, setDrawnFrom]
  );

  const performDiscard = useCallback(
    async () => {
      const result = await callFunction('performDiscard');
      setDrawnCard(null);
      setDrawnFrom(null);
      return result;
    },
    [callFunction, setDrawnCard, setDrawnFrom]
  );

  const completeRound = useCallback(
    () => callFunction('completeRound'),
    [callFunction]
  );

  const endGame = useCallback(
    () => callFunction('endGame'),
    [callFunction]
  );

  return {
    createRoom,
    joinRoom,
    markReady,
    startGame,
    revealInitialCards,
    performDraw,
    performSwap,
    performDiscard,
    completeRound,
    endGame,
  };
}

export { useGameRound };
